import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ImageIcon, Map as MapIcon, MapPin, Building2 } from 'lucide-react'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Select } from 'components/ui/select'
import { Button } from 'components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from 'components/ui/dialog'
import { ImageIcon as ImageSearchIcon } from 'lucide-react'
import ImagePickerPopup from 'components/popups/ImagePickerPopup'
import { AddressAutocomplete } from 'components/AddressAutocomplete'
import type { AddressAutocompleteHandle, AddressResult } from 'components/AddressAutocomplete'
import { MapPointPicker } from 'components/MapPointPicker'
import type { CapturedSiteFields } from 'components/MapPointPicker'
import { uploadToCloudinary } from 'components/FileUploader/fileUploadUtils'
import { blankNoAutofillFields, noAutofillProps } from 'lib/noAutofill'
import { cn } from 'lib/utils'

const locationTypes = [
	{ value: 'Commercial Center', label: 'Commercial Center' },
	{ value: 'Commercial Location', label: 'Commercial Location' },
	{ value: 'Retail Center', label: 'Retail Center' },
	{ value: 'Retail Location', label: 'Retail Location' },
]

const initialValues = {
	id: 0,
	user: '',
	assetName: '',
	assetType: 'location',
	locationType: 'Retail Location',
	retailer: '',
	address: '',
	city: '',
	stateProvince: '',
	postalCode: '',
	cbsa: '',
	country: '',
	latitude: '',
	longitude: '',
	sellingSqft: '',
	image: null as string | null,
	logo: null,
	googlePlaceId: null as string | null,
	updateNote: 'New Location',
}

/** Which pane of the popup is showing. Mirrors the New Site dialog. */
type FormMode = 'form' | 'map'

interface LocationAddNewCNProps {
	locationAdd: (values: typeof initialValues, resetForm: () => void) => void
	/**
	 * Lets the surrounding dialog widen for the map pane. A 700px map is too
	 * cramped to place a pin accurately, which is the whole point of the pane.
	 */
	onModeChange?: (mode: FormMode) => void
}

const LocationAddNewCN = ({ locationAdd, onModeChange }: LocationAddNewCNProps) => {
	const [mode, setMode] = useState<FormMode>('form')
	const [picLoaded, setPicLoaded] = useState(false)
	const [placeId, setPlaceId] = useState<string | null>(null)
	const [openImagePicker, setOpenImagePicker] = useState(false)
	const [values, setValues] = useState({ ...initialValues })
	const [errors, setErrors] = useState<Record<string, string>>({})
	const formRef = useRef<HTMLFormElement>(null)
	const searchRef = useRef<AddressAutocompleteHandle>(null)

	useEffect(() => {
		onModeChange?.(mode)
	}, [mode, onModeChange])

	const resetForm = () => {
		// Blank the DOM before React does. Chromium reads a filled address form
		// that is cleared or torn down as a submitted one and answers with its
		// "Update address?" bubble; emptying the fields first leaves it nothing
		// to offer. See `lib/noAutofill`.
		blankNoAutofillFields(formRef.current)
		// Clearing the search box imperatively rather than by remounting it.
		// The old `key` bump removed the input the user last touched from the
		// DOM, which was the second trigger for the same bubble.
		searchRef.current?.clear()
		setValues({ ...initialValues })
		setErrors({})
		setPicLoaded(false)
		setPlaceId(null)
		setMode('form')
	}

	const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		const { name, value } = e.target
		setValues(prev => ({ ...prev, [name]: value }))
	}

	const validate = () => {
		const temp: Record<string, string> = {}
		temp.assetName = values.assetName ? '' : '*Required field'
		temp.address = values.address ? '' : '*Required field'
		temp.city = values.city ? '' : '*Required field'
		temp.stateProvince = values.stateProvince ? '' : '*Required field'
		temp.postalCode = values.postalCode ? '' : '*Required field'
		setErrors(temp)
		return Object.values(temp).every(x => x === '')
	}

	const handleAddressSelect = async (result: AddressResult) => {
		const street = [result.address.house_number, result.address.road]
			.filter(Boolean)
			.join(' ')
			.trim()
		setValues(prev => ({
			...prev,
			googlePlaceId: result.placeId || null,
			assetName: result.name || prev.assetName,
			address: street,
			postalCode: result.address.postcode || '',
			city: result.address.city || '',
			stateProvince: result.address.state || '',
			country: result.address.country || '',
			latitude: String(result.latitude),
			longitude: String(result.longitude),
		}))
		setPlaceId(result.placeId || null)

		// Auto-pull a Google Places photo on selection. Honours
		// project_photo_requirement.md ("auto-pull a location image"). Failures
		// are silent — the user can still pick manually via ImagePickerPopup.
		if (result.photoUrl) {
			try {
				const upload = await uploadToCloudinary(result.photoUrl, { resourceType: 'image' })
				setValues(prev => ({ ...prev, image: upload.secure_url }))
				setPicLoaded(true)
			} catch (err) {
				console.warn('[LocationAddNewCN] Auto photo upload failed', err)
			}
		}
	}

	/**
	 * Write a map capture into the form and return to it.
	 *
	 * A name the user has already typed wins over whatever is nearest the pin,
	 * and a capture never clears a field it has nothing to put there. Those are
	 * the same rules the New Site dialog uses.
	 */
	const handleCapture = useCallback((captured: CapturedSiteFields) => {
		setValues(prev => ({
			...prev,
			latitude: String(captured.latitude),
			longitude: String(captured.longitude),
			assetName: prev.assetName || captured.name || '',
			address: captured.address || prev.address,
			city: captured.city || prev.city,
			stateProvince: captured.state || prev.stateProvince,
			postalCode: captured.postalCode || prev.postalCode,
			googlePlaceId: captured.googlePlaceId || prev.googlePlaceId,
		}))
		if (captured.googlePlaceId) setPlaceId(captured.googlePlaceId)
		setMode('form')
	}, [])

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		if (validate() && picLoaded) {
			locationAdd(values, resetForm)
		}
	}

	// Falls back to a text search when a map capture left us without a place
	// id, so the image picker still has something to look photos up by.
	const imageSearchQuery = [values.assetName, values.address, values.city, values.stateProvince]
		.filter(Boolean)
		.join(' ')

	return (
		<div className="space-y-4">
			{/* Form/Map switch. The panes are rendered conditionally rather than
			    kept mounted, so the map is torn down in form mode instead of
			    holding a WebGL context. */}
			<div className="flex justify-end">
				<div className="flex flex-shrink-0 items-center rounded-lg bg-gray-100 p-0.5">
					{(['form', 'map'] as const).map(paneMode => (
						<button
							key={paneMode}
							type="button"
							onClick={() => setMode(paneMode)}
							className={cn(
								'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
								mode === paneMode
									? 'bg-white text-[#0080fe] shadow-sm'
									: 'text-gray-500 hover:text-gray-700',
							)}
						>
							{paneMode === 'form' ? (
								<Building2 className="h-3.5 w-3.5" />
							) : (
								<MapIcon className="h-3.5 w-3.5" />
							)}
							{paneMode === 'form' ? 'Form' : 'Map'}
						</button>
					))}
				</div>
			</div>

			{mode === 'map' && (
				<MapPointPicker
					initialLatitude={values.latitude ? Number(values.latitude) : undefined}
					initialLongitude={values.longitude ? Number(values.longitude) : undefined}
					onCapture={handleCapture}
					captureLabel="Capture Location"
				/>
			)}

			{/*
				The form stays mounted in map mode and is hidden with CSS instead.
				Unmounting it would remove a filled address form from the DOM,
				which is the same thing Chromium reads as a submission. The MAP is
				still torn down conditionally above, so nothing holds a WebGL
				context it is not using.
			*/}
			<form
				ref={formRef}
				onSubmit={handleSubmit}
				className={cn('space-y-4', mode !== 'form' && 'hidden')}
				autoComplete="off"
			>
					{/* Guide text */}
					{!values.image && (
						<p className="text-xs italic text-[#0080fe]">
							{`In the search box below type in a store name, address, city, or zip code. Once you've found your desired location, select it from the dropdown. No address yet? Switch to Map and drop a pin.`}
						</p>
					)}

					{/* Row 1: Search + Location Type */}
					<div className="grid grid-cols-12 gap-3">
						<div className="col-span-8">
							<Label htmlFor="placeSearch" className="mb-1.5 block">Search Location</Label>
							<AddressAutocomplete
								ref={searchRef}
								placeholder="Search for a location..."
								onSelect={handleAddressSelect}
							/>
						</div>
						<div className="col-span-4">
							<Label htmlFor="locationType" className="mb-1.5 block">Location Type</Label>
							<Select
								id="locationType"
								name="locationType"
								value={values.locationType}
								onChange={handleChange}
							>
								{locationTypes.map(opt => (
									<option key={opt.value} value={opt.value}>{opt.label}</option>
								))}
							</Select>
						</div>
					</div>

					{/* Row 2: Location Name + Address */}
					<div className="grid grid-cols-12 gap-3">
						<div className="col-span-6">
							<Label htmlFor="assetName" className="mb-1.5 block">Location Name</Label>
							<Input
								id="assetName"
								name="assetName"
								value={values.assetName}
								onChange={handleChange}
								{...noAutofillProps}
							/>
							{errors.assetName && <p className="mt-1 text-xs text-red-500">{errors.assetName}</p>}
						</div>
						<div className="col-span-6">
							<Label htmlFor="address" className="mb-1.5 block">Address</Label>
							<Input
								id="address"
								name="address"
								value={values.address}
								onChange={handleChange}
								{...noAutofillProps}
							/>
							{errors.address && <p className="mt-1 text-xs text-red-500">{errors.address}</p>}
						</div>
					</div>

					{/* Row 3: City + State + Postal Code */}
					<div className="grid grid-cols-12 gap-3">
						<div className="col-span-4">
							<Label htmlFor="city" className="mb-1.5 block">City</Label>
							<Input
								id="city"
								name="city"
								value={values.city}
								onChange={handleChange}
								{...noAutofillProps}
							/>
							{errors.city && <p className="mt-1 text-xs text-red-500">{errors.city}</p>}
						</div>
						<div className="col-span-4">
							<Label htmlFor="stateProvince" className="mb-1.5 block">State/Prov</Label>
							<Input
								id="stateProvince"
								name="stateProvince"
								value={values.stateProvince}
								onChange={handleChange}
								{...noAutofillProps}
							/>
							{errors.stateProvince && <p className="mt-1 text-xs text-red-500">{errors.stateProvince}</p>}
						</div>
						<div className="col-span-4">
							<Label htmlFor="postalCode" className="mb-1.5 block">Postal Code</Label>
							<Input
								id="postalCode"
								name="postalCode"
								value={values.postalCode}
								onChange={handleChange}
								{...noAutofillProps}
							/>
							{errors.postalCode && <p className="mt-1 text-xs text-red-500">{errors.postalCode}</p>}
						</div>
					</div>

					{/* Row 4: Latitude + Longitude + Country (visible but disabled) */}
					<div className="grid grid-cols-12 gap-3">
						<div className="col-span-4">
							<Label htmlFor="latitude" className="mb-1.5 block">Latitude</Label>
							<Input
								id="latitude"
								name="latitude"
								value={values.latitude}
								disabled
								{...noAutofillProps}
							/>
						</div>
						<div className="col-span-4">
							<Label htmlFor="longitude" className="mb-1.5 block">Longitude</Label>
							<Input
								id="longitude"
								name="longitude"
								value={values.longitude}
								disabled
								{...noAutofillProps}
							/>
						</div>
						<div className="col-span-4">
							<Label htmlFor="country" className="mb-1.5 block">Country</Label>
							<Input
								id="country"
								name="country"
								value={values.country}
								disabled
								{...noAutofillProps}
							/>
						</div>
					</div>

					{/* Image preview */}
					{values.image && (
						<div className="flex justify-center">
							<img
								className="max-h-48 rounded-lg border border-gray-200 object-cover"
								src={values.image}
								alt={values.assetName}
							/>
						</div>
					)}

					{/* Actions. Everything below the inputs is centred. */}
					<div className="flex flex-col items-center gap-2 pt-2">
						<Button
							type="button"
							variant="primary"
							isOutline
							onClick={() => setOpenImagePicker(true)}
							className="gap-2"
							disabled={!values.assetName || !values.address}
						>
							<ImageIcon className="w-4 h-4" />
							{values.image ? 'Change Image' : 'Add Image'}
						</Button>

						<p className="text-xs italic text-[#0080fe]">
							Select 'Add Image' to import an image for this location.
						</p>

						<div className="flex items-center gap-3">
							<Button
								type="button"
								variant="ghost"
								onClick={resetForm}
							>
								Clear
							</Button>
							<Button
								type="submit"
								variant="primary"
								className="gap-2"
								disabled={!picLoaded}
							>
								<MapPin className="w-4 h-4" />
								Add Location
							</Button>
						</div>
					</div>
			</form>

			{/* Image picker dialog */}
			<Dialog open={openImagePicker} onClose={() => setOpenImagePicker(false)}>
				<DialogContent className="max-w-[700px]">
					<DialogHeader>
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
								<ImageSearchIcon className="w-5 h-5" />
							</div>
							<div>
								<DialogTitle>Select Image</DialogTitle>
								<DialogDescription>Choose an image for "{values.assetName}"</DialogDescription>
							</div>
						</div>
					</DialogHeader>
					<div className="p-5">
						<ImagePickerPopup
							placeId={placeId}
							searchQuery={placeId ? null : imageSearchQuery || null}
							locationName={values.assetName}
							onImageSelected={(imageUrl: string) => {
								setValues(prev => ({ ...prev, image: imageUrl }))
								setPicLoaded(true)
								setOpenImagePicker(false)
							}}
						/>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	)
}

export default LocationAddNewCN
